import { useMemo } from "react";
import { useVehicles, Vehicle } from "./useVehicles";
import { useTrips, Trip } from "./useTrips";
import { useExpenses, Expense, INITIAL_EXPENSE_TYPES } from "./useExpenses";
import { useMaintenance } from "./useMaintenance";

export interface VehicleDecisionData {
  vehicle: Vehicle;
  totalEarnings: number;
  /** All expenses including capital (Car Purchase, Initial Repairs, Initial Fees). */
  totalExpenses: number;
  /** Operating expenses only — same basis as Dashboard cash flow and Per-Vehicle margin. */
  ongoingExpenses: number;
  netProfit: number;
  /** Ongoing expenses as % of earnings (excludes initial purchase / capital). */
  expenseRatio: number;
  monthlyProfit: number;
  tripCount: number;
  tripDays: number;
  monthsActive: number;
  utilization: number; // trip days / total days active
  maintenanceCount: number;
  recentExpenseChange: number; // % change in last month vs prior
  lastMonthExpenses: number;
  priorMonthExpenses: number;
}

export interface Alert {
  id: string;
  vehicleId: string;
  vehicleName: string;
  type: "underperforming" | "expense_spike" | "low_utilization";
  title: string;
  reason: string;
  comparedTo: string;
  confidence: "high" | "medium";
  metrics: Record<string, number | string>;
}

export interface Opportunity {
  id: string;
  vehicleId?: string;
  vehicleName?: string;
  title: string;
  reason: string;
  estimatedImpact: string;
  effort: "low" | "medium" | "high";
}

function getMonthsActive(trips: Trip[]): number {
  const dates = trips
    .filter((t) => t.trip_start)
    .map((t) => new Date(t.trip_start!).getTime());
  if (dates.length === 0) return 0;
  const earliest = Math.min(...dates);
  const diff = Date.now() - earliest;
  return Math.max(1, Math.round(diff / (1000 * 60 * 60 * 24 * 30)));
}

function getExpensesByMonth(expenses: Expense[]) {
  const ongoing = expenses.filter((e) => !INITIAL_EXPENSE_TYPES.includes(e.expense_type || ""));
  const now = new Date();
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
  const priorMonthStart = new Date(now.getFullYear(), now.getMonth() - 2, 1);
  const priorMonthEnd = new Date(now.getFullYear(), now.getMonth() - 1, 0);

  let lastMonth = 0;
  let priorMonth = 0;
  for (const e of ongoing) {
    const d = new Date(e.expense_date);
    if (d >= lastMonthStart && d <= lastMonthEnd) lastMonth += e.amount;
    if (d >= priorMonthStart && d <= priorMonthEnd) priorMonth += e.amount;
  }
  return { lastMonth, priorMonth };
}

export const useDecisionMetrics = () => {
  const { vehicles, isLoading: vLoading } = useVehicles();
  const { trips, isLoading: tLoading } = useTrips();
  const { expenses, isLoading: eLoading } = useExpenses();
  const { tasks, isLoading: mLoading } = useMaintenance();

  const isLoading = vLoading || tLoading || eLoading || mLoading;

  const vehicleData = useMemo<VehicleDecisionData[]>(() => {
    if (!vehicles?.length) return [];
    return vehicles.map((vehicle) => {
      const vTrips = trips?.filter((t) => t.vehicle_id === vehicle.id) || [];
      const vExpenses = expenses?.filter((e) => e.vehicle_id === vehicle.id) || [];
      const vMaintenance = tasks?.filter((t) => t.vehicle_id === vehicle.id) || [];

      const totalEarnings = vTrips.reduce((s, t) => s + (t.total_earnings || t.revenue || 0), 0);
      const totalExpenses = vExpenses.reduce((s, e) => s + (e.amount || 0), 0);
      const ongoingExpenses = vExpenses
        .filter((e) => !INITIAL_EXPENSE_TYPES.includes(e.expense_type || ""))
        .reduce((s, e) => s + (e.amount || 0), 0);
      const netProfit = totalEarnings - ongoingExpenses;
      const expenseRatio = totalEarnings > 0 ? (ongoingExpenses / totalEarnings) * 100 : 0;
      const tripDays = vTrips.reduce((s, t) => s + (t.trip_days || 0), 0);
      const monthsActive = getMonthsActive(vTrips);
      const totalDaysActive = monthsActive * 30;
      const utilization = totalDaysActive > 0 ? (tripDays / totalDaysActive) * 100 : 0;
      const monthlyProfit = monthsActive > 0 ? netProfit / monthsActive : 0;
      const { lastMonth, priorMonth } = getExpensesByMonth(vExpenses);
      const recentExpenseChange = priorMonth > 0 ? ((lastMonth - priorMonth) / priorMonth) * 100 : 0;

      return {
        vehicle,
        totalEarnings,
        totalExpenses,
        ongoingExpenses,
        netProfit,
        expenseRatio,
        monthlyProfit,
        tripCount: vTrips.length,
        tripDays,
        monthsActive,
        utilization,
        maintenanceCount: vMaintenance.length,
        recentExpenseChange,
        lastMonthExpenses: lastMonth,
        priorMonthExpenses: priorMonth,
      };
    });
  }, [vehicles, trips, expenses, tasks]);

  // Fleet averages
  const fleetAvg = useMemo(() => {
    if (!vehicleData.length) return { monthlyProfit: 0, expenseRatio: 0, utilization: 0 };
    const sum = vehicleData.reduce(
      (a, v) => ({
        monthlyProfit: a.monthlyProfit + v.monthlyProfit,
        expenseRatio: a.expenseRatio + v.expenseRatio,
        utilization: a.utilization + v.utilization,
      }),
      { monthlyProfit: 0, expenseRatio: 0, utilization: 0 }
    );
    const n = vehicleData.length;
    return {
      monthlyProfit: sum.monthlyProfit / n,
      expenseRatio: sum.expenseRatio / n,
      utilization: sum.utilization / n,
    };
  }, [vehicleData]);

  // Alerts
  const alerts = useMemo<Alert[]>(() => {
    if (vehicleData.length < 2) return [];
    const result: Alert[] = [];

    for (const v of vehicleData) {
      if (v.tripCount === 0) continue;
      const name = v.vehicle.name || `${v.vehicle.year} ${v.vehicle.make} ${v.vehicle.model}`;

      // Underperforming vs fleet
      if (v.monthlyProfit < fleetAvg.monthlyProfit * 0.7 && v.monthsActive >= 2) {
        result.push({
          id: `under-${v.vehicle.id}`,
          vehicleId: v.vehicle.id,
          vehicleName: name,
          type: "underperforming",
          title: `${name} is underperforming compared to your fleet`,
          reason: `Monthly profit of $${Math.round(v.monthlyProfit)} is ${Math.round(((fleetAvg.monthlyProfit - v.monthlyProfit) / fleetAvg.monthlyProfit) * 100)}% below fleet average.`,
          comparedTo: `Fleet average: $${Math.round(fleetAvg.monthlyProfit)}/mo`,
          confidence: v.monthsActive >= 3 ? "high" : "medium",
          metrics: { monthlyProfit: v.monthlyProfit, fleetAvg: fleetAvg.monthlyProfit },
        });
      }

      // Expense spike
      if (v.recentExpenseChange > 25 && v.lastMonthExpenses > 50) {
        result.push({
          id: `spike-${v.vehicle.id}`,
          vehicleId: v.vehicle.id,
          vehicleName: name,
          type: "expense_spike",
          title: `${name} expenses spiked ${Math.round(v.recentExpenseChange)}% last month`,
          reason: `Last month: $${Math.round(v.lastMonthExpenses)} vs prior month: $${Math.round(v.priorMonthExpenses)}.`,
          comparedTo: `Prior month baseline: $${Math.round(v.priorMonthExpenses)}`,
          confidence: v.recentExpenseChange > 50 ? "high" : "medium",
          metrics: { lastMonth: v.lastMonthExpenses, priorMonth: v.priorMonthExpenses },
        });
      }

      // Low utilization
      if (v.utilization < fleetAvg.utilization * 0.6 && v.monthsActive >= 2) {
        result.push({
          id: `util-${v.vehicle.id}`,
          vehicleId: v.vehicle.id,
          vehicleName: name,
          type: "low_utilization",
          title: `${name} has low utilization`,
          reason: `${Math.round(v.utilization)}% utilization vs ${Math.round(fleetAvg.utilization)}% fleet average.`,
          comparedTo: `Fleet average utilization: ${Math.round(fleetAvg.utilization)}%`,
          confidence: v.monthsActive >= 3 ? "high" : "medium",
          metrics: { utilization: v.utilization, fleetAvg: fleetAvg.utilization },
        });
      }
    }

    return result;
  }, [vehicleData, fleetAvg]);

  // Opportunities
  const opportunities = useMemo<Opportunity[]>(() => {
    if (!vehicleData.length) return [];
    const result: Opportunity[] = [];

    // Sort by expense ratio (highest first) for maintenance cost opportunity
    const highExpenseRatio = [...vehicleData]
      .filter((v) => v.expenseRatio > 60 && v.ongoingExpenses > 100)
      .sort((a, b) => b.expenseRatio - a.expenseRatio);

    for (const v of highExpenseRatio.slice(0, 2)) {
      const name = v.vehicle.name || `${v.vehicle.year} ${v.vehicle.make} ${v.vehicle.model}`;
      const potentialSaving = Math.round(v.ongoingExpenses * 0.15);
      result.push({
        id: `exp-${v.vehicle.id}`,
        vehicleId: v.vehicle.id,
        vehicleName: name,
        title: `Address ${name}'s high expense ratio`,
        reason: `Ongoing expenses are ${Math.round(v.expenseRatio)}% of earnings (capital purchase excluded). Reviewing costs could improve margins.`,
        estimatedImpact: `$${potentialSaving}–$${potentialSaving * 2} potential savings`,
        effort: "medium",
      });
    }

    // Low utilization → repricing opportunity
    const lowUtil = [...vehicleData]
      .filter((v) => v.utilization < 40 && v.tripCount > 0)
      .sort((a, b) => a.utilization - b.utilization);

    for (const v of lowUtil.slice(0, 2)) {
      const name = v.vehicle.name || `${v.vehicle.year} ${v.vehicle.make} ${v.vehicle.model}`;
      result.push({
        id: `price-${v.vehicle.id}`,
        vehicleId: v.vehicle.id,
        vehicleName: name,
        title: `Reprice ${name} for better utilization`,
        reason: `Only ${Math.round(v.utilization)}% utilized. Adjusting pricing or availability could attract more bookings.`,
        estimatedImpact: "$50–$200/mo additional revenue",
        effort: "low",
      });
    }

    // Expense spike outliers
    const spikers = vehicleData.filter((v) => v.recentExpenseChange > 30 && v.lastMonthExpenses > 50);
    for (const v of spikers.slice(0, 1)) {
      const name = v.vehicle.name || `${v.vehicle.year} ${v.vehicle.make} ${v.vehicle.model}`;
      result.push({
        id: `inv-${v.vehicle.id}`,
        vehicleId: v.vehicle.id,
        vehicleName: name,
        title: `Investigate ${name}'s expense outliers`,
        reason: `Expenses jumped ${Math.round(v.recentExpenseChange)}% last month. Worth checking what drove the increase.`,
        estimatedImpact: `$${Math.round(v.lastMonthExpenses - v.priorMonthExpenses)} potential monthly reduction`,
        effort: "low",
      });
    }

    return result.sort((a, b) => {
      const effortOrder = { low: 0, medium: 1, high: 2 };
      return effortOrder[a.effort] - effortOrder[b.effort];
    });
  }, [vehicleData]);

  return { vehicleData, fleetAvg, alerts, opportunities, isLoading };
};
